/**
 * Apollo Client Configuration
 * @implements FS-003 - GraphQL API Architecture
 * @implements FS-003.5.1 - HTTP Request Authentication
 * @implements FS-003.5.2 - WebSocket Authentication
 * @implements FS-003.3.1 - Real-Time Updates (Subscriptions)
 *
 * This module configures Apollo Client with:
 * - HTTP link for queries and mutations
 * - WebSocket link for subscriptions (using graphql-ws)
 * - Split link to route operations to the correct transport
 * - Authentication token injection for both transports
 */

import {
  ApolloClient,
  InMemoryCache,
  ApolloLink,
  HttpLink,
  split,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createClient } from 'graphql-ws';

/**
 * Get the authentication token from storage
 * This will be replaced with actual auth token retrieval in TS-044/TS-045
 */
const getAuthToken = (): string | null => {
  // TODO: Implement actual token retrieval from AuthProvider (TS-044, TS-045)
  return localStorage.getItem('authToken');
};

/**
 * HTTP Link for queries and mutations
 * Uses the Vite proxy in development (/graphql -> localhost:3000/graphql)
 */
const httpLink = new HttpLink({
  uri: '/graphql',
  credentials: 'include', // Include cookies for authentication
});

/**
 * Authentication link to add Bearer token to requests
 * @implements FS-003.5.1 - Token format is `Bearer <token>`
 */
const authLink = new ApolloLink((operation, forward) => {
  const token = getAuthToken();

  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  }));

  return forward(operation);
});

/**
 * WebSocket Link for subscriptions
 * @implements FS-003.5.2 - Token passed in connection parameters
 * @implements BS-003.5 - Uses graphql-ws protocol
 */
const wsLink = new GraphQLWsLink(
  createClient({
    url: `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${
      window.location.host
    }/graphql`,
    connectionParams: () => {
      const token = getAuthToken();
      // Use lowercase 'authorization' to match backend expectation
      // @implements FS-003.5.2 - WebSocket Authentication
      return token ? { authorization: `Bearer ${token}` } : {};
    },
    // Reconnection handling
    retryAttempts: 5,
    shouldRetry: () => true,
    // Connection lifecycle callbacks
    on: {
      connected: () => {
        console.log('[Apollo WS] Connected');
      },
      closed: (event) => {
        console.log('[Apollo WS] Closed', event);
      },
      error: (error) => {
        console.error('[Apollo WS] Error', error);
      },
    },
  }),
);

/**
 * Error link for global error handling
 * @implements FS-003.7.2 - Error Information
 * Handles GraphQL and network errors globally
 */
const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, extensions, path }) => {
      const code = extensions?.code as string | undefined;

      // Handle authentication errors globally
      if (code === 'UNAUTHENTICATED') {
        // Dispatch custom event for AuthProvider to handle
        window.dispatchEvent(new CustomEvent('auth:unauthorized'));
        console.warn('[Apollo] Session expired or invalid');
      } else {
        console.error(
          `[GraphQL error]: Message: ${message}, Code: ${
            code || 'UNKNOWN'
          }, Path: ${path?.join('.')}`,
        );
      }
    });
  }

  if (networkError) {
    console.error(`[Network error]: ${networkError.message}`);
    // Dispatch network error event for global handling
    window.dispatchEvent(
      new CustomEvent('network:error', { detail: networkError }),
    );
  }
});

/**
 * Split link to route operations to the correct transport
 * - Subscriptions go through WebSocket
 * - Queries and mutations go through HTTP
 */
const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    );
  },
  wsLink,
  errorLink.concat(authLink.concat(httpLink)),
);

/**
 * Apollo Client instance
 * Uses InMemoryCache as the state management solution (no MobX needed)
 */
export const apolloClient = new ApolloClient({
  link: splitLink,
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          // Don't cache 'me' query - always fetch fresh for auth state
          me: {
            merge: false,
          },
        },
      },
      // Cache User objects by their ID
      User: {
        keyFields: ['id'],
      },
    },
  }),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
    },
    query: {
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    },
    mutate: {
      errorPolicy: 'all',
    },
  },
  // Enable devtools in development
  connectToDevTools: import.meta.env.DEV,
});
